import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import DjamannLoader from '../components/DjamannLoader';
import logger from '../utils/logger';
import {
  AlertTriangle, Ban, Mail, MessageSquareWarning, CreditCard, Copyright,
  HelpCircle, Clock, RefreshCw, CheckCircle2, XCircle, Trash2, UserX, Minus,
  Search, ClipboardList, ArrowLeft, Eye, Settings, X, User, Target, Calendar,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Clapperboard
} from 'lucide-react';
import { toast } from '../components/Toast';
import { confirm } from '../components/ConfirmDialog';

const AdminReports = () => {
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('en_attente');
  const [selectedReport, setSelectedReport] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionForm, setActionForm] = useState({ action: 'avertissement', notes: '' });
  const [actionLoading, setActionLoading] = useState(false);
  
  // Recherche et pagination
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [reportsRes, statsRes] = await Promise.all([
        api.get(`/reports${filter ? `?statut=${filter}` : ''}`),
        api.get('/reports/stats')
      ]);
      const reportsData = reportsRes.data;
      setReports(Array.isArray(reportsData) ? reportsData : []);
      setStats(statsRes.data || {});
    } catch (err) {
      logger.error('Erreur chargement:', err);
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, search]);

  const filteredReports = reports.filter(r => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      (r.target_titre || '').toLowerCase().includes(searchLower) ||
      (r.reporter_nom || '').toLowerCase().includes(searchLower) ||
      (r.reporter_email || '').toLowerCase().includes(searchLower) ||
      (r.author_nom || '').toLowerCase().includes(searchLower) ||
      (r.reason || '').toLowerCase().includes(searchLower) ||
      (r.description || '').toLowerCase().includes(searchLower)
    );
  });

  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);
  const paginatedReports = filteredReports.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const openActionModal = (report) => {
    setSelectedReport(report);
    setActionForm({ action: 'avertissement', notes: '' });
    setShowActionModal(true);
  };

  const handleProcess = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      await api.put(`/reports/${selectedReport.id}/process`, actionForm);
      setShowActionModal(false);
      loadData();
    } catch (err) {
      toast.error('Erreur lors du traitement');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (reportId) => {
    if (!(await confirm('Voulez-vous vraiment rejeter ce signalement ?'))) return;
    try {
      await api.put(`/reports/${reportId}/reject`, { notes: '' });
      loadData();
    } catch (err) {
      toast.error('Erreur lors du rejet');
    }
  };

  const handleLiftSuspension = async (reportId) => {
    if (!(await confirm('Voulez-vous vraiment lever la suspension ?'))) return;
    try {
      await api.put(`/reports/${reportId}/process`, { action: 'lever_suspension', notes: '' });
      loadData();
    } catch (err) {
      toast.error('Erreur lors de la levée de suspension');
    }
  };

  const getReasonConfig = (reason) => {
    const config = {
      'contenu_illicite': { icon: <Ban size={16} className="text-red-500" />, label: 'Contenu illicite', color: 'red' },
      'spam': { icon: <Mail size={16} className="text-orange-500" />, label: 'Spam', color: 'orange' },
      'harcelement': { icon: <MessageSquareWarning size={16} className="text-red-500" />, label: 'Harcèlement', color: 'red' },
      'fraude': { icon: <CreditCard size={16} className="text-red-500" />, label: 'Fraude', color: 'red' },
      'droits_auteur': { icon: <Copyright size={16} className="text-purple-500" />, label: 'Droits d\'auteur', color: 'purple' },
      'autre': { icon: <HelpCircle size={16} className="text-gray-500" />, label: 'Autre', color: 'gray' }
    };
    return config[reason] || { icon: <HelpCircle size={16} className="text-gray-500" />, label: reason, color: 'gray' };
  };

  const getStatusConfig = (statut) => {
    const config = {
      'en_attente': { icon: <Clock size={16} />, label: 'En attente', bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
      'en_cours': { icon: <RefreshCw size={16} />, label: 'En cours', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
      'resolu': { icon: <CheckCircle2 size={16} />, label: 'Résolu', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
      'rejete': { icon: <XCircle size={16} />, label: 'Rejeté', bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300' }
    };
    return config[statut] || config['en_attente'];
  };

  const getActionConfig = (action) => {
    const config = {
      'aucune': { icon: <Minus size={16} className="text-gray-500" />, label: 'Aucune action', color: 'gray' },
      'avertissement': { icon: <AlertTriangle size={16} className="text-yellow-500" />, label: 'Avertissement envoyé', color: 'yellow' },
      'suspension_projet': { icon: <Ban size={16} className="text-orange-500" />, label: 'Projet suspendu', color: 'orange' },
      'lever_suspension': { icon: <CheckCircle2 size={16} className="text-green-500" />, label: 'Suspension levée', color: 'green' },
      'suppression_projet': { icon: <Trash2 size={16} className="text-red-500" />, label: 'Projet supprimé', color: 'red' },
      'suspension_compte': { icon: <UserX size={16} className="text-red-500" />, label: 'Compte suspendu', color: 'red' }
    };
    return config[action] || { icon: <HelpCircle size={16} className="text-gray-500" />, label: action, color: 'gray' };
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (hours < 1) return 'Il y a quelques minutes';
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days} jour${days > 1 ? 's' : ''}`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return <DjamannLoader variant="fullscreen" text="Chargement des signalements..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50 to-orange-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center shadow-lg shadow-red-200">
                <AlertTriangle size={24} className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Signalements</h1>
                <p className="text-sm text-gray-500">Modération du contenu signalé</p>
              </div>
            </div>
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-medium"
            >
              <ArrowLeft size={18} /> Tableau de bord
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div 
            onClick={() => setFilter('en_attente')}
            className={`bg-white rounded-2xl p-5 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${filter === 'en_attente' ? 'ring-2 ring-amber-400 shadow-lg' : 'shadow'}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Clock size={20} className="text-amber-600" />
              </div>
              {stats.en_attente > 0 && (
                <span className="px-2 py-1 bg-amber-500 text-white text-xs font-bold rounded-full animate-pulse">
                  {stats.en_attente}
                </span>
              )}
            </div>
            <div className="text-3xl font-bold text-amber-600">{stats.en_attente || 0}</div>
            <div className="text-sm text-gray-500 mt-1">En attente</div>
          </div>

          <div 
            onClick={() => setFilter('en_cours')}
            className={`bg-white rounded-2xl p-5 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${filter === 'en_cours' ? 'ring-2 ring-blue-400 shadow-lg' : 'shadow'}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <RefreshCw size={20} className="text-blue-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-blue-600">{stats.en_cours || 0}</div>
            <div className="text-sm text-gray-500 mt-1">En cours</div>
          </div>

          <div 
            onClick={() => setFilter('resolu')}
            className={`bg-white rounded-2xl p-5 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${filter === 'resolu' ? 'ring-2 ring-green-400 shadow-lg' : 'shadow'}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <CheckCircle2 size={20} className="text-green-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-green-600">{stats.resolu || 0}</div>
            <div className="text-sm text-gray-500 mt-1">Résolus</div>
          </div>

          <div 
            onClick={() => setFilter('rejete')}
            className={`bg-white rounded-2xl p-5 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${filter === 'rejete' ? 'ring-2 ring-gray-400 shadow-lg' : 'shadow'}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                <XCircle size={20} className="text-gray-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-600">{stats.rejete || 0}</div>
            <div className="text-sm text-gray-500 mt-1">Rejetés</div>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="bg-white rounded-2xl shadow-sm mb-6 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par titre, auteur, raison..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400 transition"
                />
              </div>
              <button
                onClick={() => setFilter('')}
                className={`px-6 py-3 rounded-xl font-medium transition flex items-center gap-2 ${
                  filter === '' ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <ClipboardList size={18} /> Voir tout
              </button>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex overflow-x-auto">
            {[
              { key: 'en_attente', icon: <Clock size={16} />, label: 'En attente' },
              { key: 'en_cours', icon: <RefreshCw size={16} />, label: 'En cours' },
              { key: 'resolu', icon: <CheckCircle2 size={16} />, label: 'Résolus' },
              { key: 'rejete', icon: <XCircle size={16} />, label: 'Rejetés' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`flex-shrink-0 px-6 py-4 font-medium transition-all border-b-2 flex items-center gap-2 ${
                  filter === tab.key 
                    ? 'text-red-600 border-red-500 bg-red-50/50' 
                    : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {tab.icon}
                {tab.label}
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  filter === tab.key ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'
                }`}>
                  {stats[tab.key] || 0}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Results Info */}
        {filteredReports.length > 0 && (
          <div className="flex items-center justify-between mb-4 px-2">
            <p className="text-sm text-gray-500">
              <span className="font-medium text-gray-700">{filteredReports.length}</span> signalement{filteredReports.length > 1 ? 's' : ''}
              {search && <span> pour "<span className="font-medium">{search}</span>"</span>}
            </p>
          </div>
        )}

        {/* Reports List */}
        <div className="space-y-4">
          {paginatedReports.length > 0 ? (
            paginatedReports.map(report => {
              const statusConfig = getStatusConfig(report.statut);
              const reasonConfig = getReasonConfig(report.reason);
              
              return (
                <div 
                  key={report.id} 
                  className={`bg-white rounded-2xl shadow-sm hover:shadow-md transition-all overflow-hidden ${
                    report.statut === 'en_attente' ? 'border-l-4 border-l-amber-400' :
                    report.statut === 'en_cours' ? 'border-l-4 border-l-blue-400' :
                    report.statut === 'resolu' ? 'border-l-4 border-l-green-400' :
                    'border-l-4 border-l-gray-300'
                  }`}
                >
                  <div className="p-6">
                    <div className="flex flex-col lg:flex-row gap-6">
                      {/* Main Content */}
                      <div className="flex-1">
                        {/* Header */}
                        <div className="flex flex-wrap items-center gap-2 mb-4">
                          <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${statusConfig.bg} ${statusConfig.text}`}>
                            {statusConfig.icon} {statusConfig.label}
                          </span>
                          <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 ${
                            report.target_type === 'project' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-indigo-100 text-indigo-700'
                          }`}>
                            {report.target_type === 'project' ? <Clapperboard size={14} /> : <ClipboardList size={14} />}
                            {report.target_type === 'project' ? 'Projet' : 'Tâche'}
                          </span>
                          <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                            reasonConfig.color === 'red' ? 'bg-red-100 text-red-700' :
                            reasonConfig.color === 'orange' ? 'bg-orange-100 text-orange-700' :
                            reasonConfig.color === 'purple' ? 'bg-purple-100 text-purple-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {reasonConfig.icon} {reasonConfig.label}
                          </span>
                          <span className="text-xs text-gray-400 ml-auto">#{report.id}</span>
                        </div>

                        {/* Title */}
                        <h3 className="text-lg font-bold text-gray-800 mb-3">
                          {report.target_titre || `${report.target_type} #${report.target_id}`}
                        </h3>

                        {/* Description */}
                        {report.description && (
                          <div className="bg-gray-50 rounded-xl p-4 mb-4">
                            <p className="text-sm text-gray-700 italic">"{report.description}"</p>
                          </div>
                        )}

                        {/* Info Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center gap-2 text-gray-600">
                            <span className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                              <User size={16} className="text-blue-500" />
                            </span>
                            <div>
                              <span className="text-gray-400 text-xs">Signalé par</span>
                              <p className="font-medium text-gray-800">{report.reporter_nom}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <span className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                              <Target size={16} className="text-red-500" />
                            </span>
                            <div>
                              <span className="text-gray-400 text-xs">Auteur du contenu</span>
                              <p className="font-medium text-gray-800">{report.author_nom}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <span className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center">
                              <Calendar size={16} className="text-gray-500" />
                            </span>
                            <div>
                              <span className="text-gray-400 text-xs">Signalé</span>
                              <p className="font-medium text-gray-800">{formatDate(report.created_at)}</p>
                            </div>
                          </div>
                          {report.reporter_email && (
                            <div className="flex items-center gap-2 text-gray-600">
                              <span className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                                <Mail size={16} className="text-green-500" />
                              </span>
                              <div>
                                <span className="text-gray-400 text-xs">Contact</span>
                                <p className="font-medium text-gray-800 text-xs">{report.reporter_email}</p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Action Taken */}
                        {report.admin_action && (
                          <div className={`mt-4 p-4 rounded-xl ${
                            report.admin_action === 'lever_suspension' ? 'bg-green-50 border border-green-200' :
                            report.admin_action === 'suspension_projet' || report.admin_action === 'suspension_compte' ? 'bg-orange-50 border border-orange-200' :
                            report.admin_action === 'suppression_projet' ? 'bg-red-50 border border-red-200' :
                            'bg-blue-50 border border-blue-200'
                          }`}>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-lg">{getActionConfig(report.admin_action).icon}</span>
                              <span className="font-semibold text-gray-800">{getActionConfig(report.admin_action).label}</span>
                            </div>
                            {report.admin_notes && (
                              <p className="text-sm text-gray-600 mb-2 flex items-center gap-1">
                                <MessageSquareWarning size={14} /> {report.admin_notes}
                              </p>
                            )}
                            {report.treated_at && (
                              <p className="text-xs text-gray-400">
                                Traité le {new Date(report.treated_at).toLocaleDateString('fr-FR')} par {report.admin_nom}
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Actions Sidebar */}
                      <div className="lg:w-48 flex flex-row lg:flex-col gap-2">
                        <Link
                          to={report.target_type === 'project' ? `/project/${report.target_id}` : `/project/${report.task_project_id}`}
                          className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-medium text-sm"
                        >
                          <Eye size={16} /> <span className="hidden sm:inline">Voir</span>
                        </Link>
                        
                        {(report.statut === 'en_attente' || report.statut === 'en_cours') && (
                          <>
                            <button
                              onClick={() => openActionModal(report)}
                              className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-xl hover:from-red-600 hover:to-orange-600 transition font-medium text-sm shadow-lg shadow-red-200"
                            >
                              <Settings size={16} /> <span className="hidden sm:inline">Traiter</span>
                            </button>
                            <button
                              onClick={() => handleReject(report.id)}
                              className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition font-medium text-sm"
                            >
                              <XCircle size={16} /> <span className="hidden sm:inline">Rejeter</span>
                            </button>
                          </>
                        )}
                        
                        {report.admin_action === 'suspension_projet' && report.statut === 'resolu' && (
                          <button
                            onClick={() => handleLiftSuspension(report.id)}
                            className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition font-medium text-sm"
                          >
                            <CheckCircle2 size={16} /> <span className="hidden sm:inline">Lever</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="bg-white rounded-2xl shadow-sm p-16 text-center">
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
                {search ? <Search size={32} className="text-gray-400" /> : 
                 filter === 'en_attente' ? <Clock size={32} className="text-amber-400" /> : 
                 filter === 'resolu' ? <CheckCircle2 size={32} className="text-green-400" /> : 
                 <ClipboardList size={32} className="text-gray-400" />}
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Aucun signalement</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                {search 
                  ? `Aucun résultat trouvé pour "${search}"`
                  : filter 
                    ? `Aucun signalement ${filter === 'en_attente' ? 'en attente' : filter === 'resolu' ? 'résolu' : filter === 'rejete' ? 'rejeté' : 'en cours'}`
                    : 'Tous les signalements ont été traités'
                }
              </p>
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="mt-4 px-6 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition"
                >
                  Effacer la recherche
                </button>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-8">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="p-2 rounded-lg bg-white shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <ChevronsLeft size={18} />
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg bg-white shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <ChevronLeft size={18} />
            </button>
            
            <div className="flex items-center gap-1">
              {(() => {
                const pages = [];
                const showPages = new Set();
                showPages.add(1);
                showPages.add(totalPages);
                for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
                  showPages.add(i);
                }
                const sortedPages = [...showPages].sort((a, b) => a - b);
                sortedPages.forEach((page, idx) => {
                  if (idx > 0 && page - sortedPages[idx - 1] > 1) {
                    pages.push(<span key={`ellipsis-${page}`} className="px-2 text-gray-400">...</span>);
                  }
                  pages.push(
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-10 h-10 rounded-lg font-medium transition ${currentPage === page ? 'bg-red-500 text-white shadow-lg' : 'bg-white shadow-sm hover:bg-gray-50 text-gray-600'}`}
                    >
                      {page}
                    </button>
                  );
                });
                return pages;
              })()}
            </div>
            
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg bg-white shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <ChevronRight size={18} />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg bg-white shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <ChevronsRight size={18} />
            </button>
          </div>
        )}
      </div>

      {/* Action Modal */}
      {showActionModal && selectedReport && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-500 to-orange-500 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
                    <Settings size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Traiter le signalement</h2>
                    <p className="text-white/80 text-sm">#{selectedReport.id}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowActionModal(false)} 
                  className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Report Summary */}
            <div className="p-6 border-b border-gray-100 bg-gray-50">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">Contenu</span>
                  <p className="font-semibold text-gray-800 truncate">{selectedReport.target_titre}</p>
                </div>
                <div>
                  <span className="text-gray-400">Auteur</span>
                  <p className="font-semibold text-gray-800">{selectedReport.author_nom}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-400">Raison</span>
                  <p className="font-semibold text-gray-800">
                    {getReasonConfig(selectedReport.reason).icon} {getReasonConfig(selectedReport.reason).label}
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleProcess} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Action à prendre
                </label>
                <select
                  value={actionForm.action}
                  onChange={(e) => setActionForm({ ...actionForm, action: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400 transition font-medium"
                >
                    <option value="avertissement">Envoyer un avertissement</option>
                    <option value="suspension_projet">Suspendre le projet/tâche</option>
                    <option value="lever_suspension">Lever la suspension</option>
                    <option value="suppression_projet">Supprimer le projet/tâche</option>
                  <option value="suspension_compte">⊘ Suspendre le compte de l'auteur</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Notes (optionnel)
                </label>
                <textarea
                  value={actionForm.notes}
                  onChange={(e) => setActionForm({ ...actionForm, notes: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 bg-gray-50 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400 transition resize-none"
                  placeholder="Ajoutez des notes sur votre décision..."
                />
              </div>

              {/* Warnings */}
              {actionForm.action === 'suspension_compte' && (
                <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                  <div className="flex items-start gap-3">
                    <AlertTriangle size={24} className="text-red-500 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-red-800">Action critique</p>
                      <p className="text-sm text-red-600">Le compte de "{selectedReport.author_nom}" sera suspendu et ne pourra plus accéder à la plateforme.</p>
                    </div>
                  </div>
                </div>
              )}

              {actionForm.action === 'suppression_projet' && (
                <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                  <div className="flex items-start gap-3">
                    <Trash2 size={24} className="text-red-500 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-red-800">Suppression définitive</p>
                      <p className="text-sm text-red-600">Le contenu signalé sera supprimé de façon permanente.</p>
                    </div>
                  </div>
                </div>
              )}

              {actionForm.action === 'lever_suspension' && (
                <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 size={24} className="text-green-500 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-green-800">Rétablissement</p>
                      <p className="text-sm text-green-600">Le projet/tâche sera rétabli et redeviendra visible sur la plateforme.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowActionModal(false)}
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-semibold"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-xl hover:from-red-600 hover:to-orange-600 transition font-semibold shadow-lg shadow-red-200 disabled:opacity-50"
                >
                  {actionLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      Traitement...
                    </span>
                  ) : (
                    'Appliquer l\'action'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReports;
